import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { Icon } from '../../../../components/common/icon/Icon';
import {
  OpenMarketProductApporovalStatusType,
  OpenMarketProductApprovalStatusConstant,
  OpenMarketProductStatusConstant,
  OpenMarketProductStatusConstantType,
} from '../../../../constants/openMarketProduct/openMarketProduct';
import { useAppSelector } from '../../../../hooks/store/useStore';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IOpenMarketProduct } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { formatDateFromTodayOnlyDifference } from '../../../../utils/formatter/formatDateFromTodayOnlyDifference.util';
import { formatPublishHistory } from '../../../../utils/formatter/formatPublishHistory.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getOpenMarketDetailPageURL } from '../../../../utils/getter/getProductDetailPageURL.util';
import { getOpenMarketProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { getRegionByDepth, RegionDepth } from '../../../../utils/getter/getShortAddress.util';
import { ProductChip } from '../components/ProductChip';

export type IOpenMarketProductForDetailCard = Pick<
  IOpenMarketProduct,
  | 'id'
  | 'updatedAt'
  | 'views'
  | 'inquiryCount'
  | 'images'
  | 'machine'
  | 'manufacturedYear'
  | 'location'
  | 'operationTime'
  | 'approvalStatus'
  | 'status'
  | 'approvalDate'
  | 'hopeSellingPrice'
  | 'pokeCount'
  | 'code'
  | 'bump'
  | 'isNewProduct'
>;

interface OpenMarketProductDetailCardProps {
  product: IOpenMarketProductForDetailCard;
  isNewBadgeVisible?: boolean;
  indexInList: number;
}

const LIKE_NEW_OPERATION_HOURS = 200;

export const OpenMarketProductDetailCard = ({
  product,
  isNewBadgeVisible = false,
  indexInList,
}: OpenMarketProductDetailCardProps) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { APPROVAL, ONGOING, REJECT } = OpenMarketProductApprovalStatusConstant;
  const { ONSALE, SOLDOUT } = OpenMarketProductStatusConstant;

  const directDealFilter = useAppSelector((state) => state.filter.directDealFilter);

  const handleClick = () => {
    if (product.approvalStatus === APPROVAL && product.status === ONSALE) {
      const url = getOpenMarketDetailPageURL(product.id, product.machine.machineType);
      navigate(url);
      // 모든 필터에 대해 활성화된 필터가 있는지 확인
      const hasAnyActiveFilter = directDealFilter.globalFilters.some((filter) => {
        let isFilterButtonActive = false;

        if (filter.optionType === 'radio' || filter.optionType === 'checkbox') {
          filter.options.forEach((option) => {
            if (option.isSelected && !option.isDefault) {
              isFilterButtonActive = true;
            }
          });
        } else if (filter.optionType === 'slider') {
          filter.options.forEach((option) => {
            if (option.value !== option.defaultValue) {
              isFilterButtonActive = true;
            }
          });
        }

        return isFilterButtonActive;
      });

      ampli.pvmClickOpenMarketDetailCardOnPlp({
        index: indexInList.toString(),
        is_any_filter_selected: hasAnyActiveFilter,
        filters: [...directDealFilter.globalFilters],
        title: `${title}, ${product.manufacturedYear.toString()}년식, ${product.operationTime.toString()}시간`,
        code: product.code,
        bump_times: product.bump?.bumpTimes || 0,
        days_since_last_bump: product.bump?.lastBumpAt
          ? differenceInDays(new Date(), new Date(product.bump.lastBumpAt))
          : 0,
        is_bumped_before: !!product.bump,
        is_new_product: product.isNewProduct,
      });
    }
  };

  const getBlurEffect = (
    status: OpenMarketProductStatusConstantType,
    approvalStatus: OpenMarketProductApporovalStatusType,
  ) => {
    const BLURRED = true;
    const NOT_BLURRED = false;
    const DIMMED_TEXT = {
      SOLDOUT: '판매 완료',
      ONGOING: '심사중',
      REJECT: '판매불가',
      DEFAULT: '',
    };

    switch (approvalStatus) {
      case APPROVAL:
        return {
          isBlurred: status === SOLDOUT ? BLURRED : NOT_BLURRED,
          dimmedText: status === SOLDOUT ? DIMMED_TEXT.SOLDOUT : DIMMED_TEXT.DEFAULT,
        };

      case ONGOING:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.ONGOING,
        };

      case REJECT:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.REJECT,
        };

      default:
        return {
          isBlurred: NOT_BLURRED,
          dimmedText: DIMMED_TEXT.DEFAULT,
        };
    }
  };
  const blurEffect = getBlurEffect(product.status, product.approvalStatus);
  const { isBlurred, dimmedText } = blurEffect;

  const isNewOpenMarketProduct = product.isNewProduct;

  const title = getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine });

  const price = useMemo<string>(() => {
    return getOpenMarketProductPrice(product.status, product.hopeSellingPrice);
  }, [product]);

  const productImageUrl = useMemo(() => {
    if (!product.images) {
      return BlankImage;
    }

    if (product.images.length === 0) {
      return BlankImage;
    }

    if (product.images.find((image) => image.type === 'LEFT')) {
      return product.images.find((image) => image.type === 'LEFT')?.imageUrl;
    }

    return product.images[0]?.imageUrl;
  }, [product.images]);

  return (
    <Container onClick={handleClick}>
      <ImageWrap>
        <Image src={productImageUrl} alt={productImageUrl} isBlurred={isBlurred} loading={'lazy'} />
        {isBlurred && <DimmedText>{dimmedText}</DimmedText>}
        {isNewBadgeVisible && isNewOpenMarketProduct && (
          <BadgeContainer>
            <ProductChip type={'NEW'} />
          </BadgeContainer>
        )}
      </ImageWrap>

      <ProductInfoSection isBlurred={isBlurred}>
        <ProductContentGroup>
          <Title>{title}</Title>
          <Price>{price}</Price>
          <LocationAndTimeContainer>
            <TractorSummaryText>{`${getRegionByDepth(product.location, RegionDepth.REGION)} · ${String(
              product.manufacturedYear,
            ).slice(2, 4)}년식 · ${Number(product.operationTime).toLocaleString()}시간`}</TractorSummaryText>
          </LocationAndTimeContainer>
        </ProductContentGroup>
        <ProductMetaContainer>
          {!isNewOpenMarketProduct && product.bump?.lastBumpAt ? (
            <PostDate color={'#FF3049'}>{`${formatDateFromTodayOnlyDifference(
              product.bump.lastBumpAt,
            )} 끌올`}</PostDate>
          ) : (
            <PostDate>{formatPublishHistory(product.approvalDate, product.updatedAt)}</PostDate>
          )}
          <MetricsGroup>
            <ViewCount>
              <Icon name={'Eye'} size={'Medium'} fill={'#C6C6D5'} />
              <Count>{Number(product.views || 0).toLocaleString()}</Count>
            </ViewCount>
            <SplitBarWrapper>
              <SplitBar>{'|'}</SplitBar>
            </SplitBarWrapper>
            <InquiryCount>
              <Icon name={'Envelope'} size={'Medium'} fill={'#C6C6D5'} />
              <Count>{Number(product.pokeCount || 0).toLocaleString()}</Count>
            </InquiryCount>
            <SplitBarWrapper>
              <SplitBar>{'|'}</SplitBar>
            </SplitBarWrapper>
            <InquiryCount>
              <Icon name={'Phone'} size={'Medium'} fill={'#C6C6D5'} />
              <Count>{Number(product.inquiryCount || 0).toLocaleString()}</Count>
            </InquiryCount>
          </MetricsGroup>
        </ProductMetaContainer>
      </ProductInfoSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 160px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  align-self: stretch;

  gap: 16px;

  cursor: pointer;
`;

const ImageWrap = styled.div`
  min-width: 150px;
  height: 100%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;

  padding: 0px 16px;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.4 : 1)};
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 8px;
  left: 8px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const DimmedText = styled.span`
  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 20.4px */

  mix-blend-mode: darken;

  opacity: 0.4;

  white-space: nowrap;
`;

const ProductInfoSection = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  padding: 4px 0px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductContentGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const LocationAndTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow: hidden;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const TractorSummaryText = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  text-overflow: ellipsis;
`;

const ProductMetaContainer = styled.div`
  display: flex;
  margin-top: auto;
  align-items: center;
  justify-content: space-between;
  align-content: center;
  align-self: stretch;
  flex-wrap: wrap;
`;

const PostDate = styled.span<{ color?: string }>`
  color: ${(props) => props.color || 'var(--Light-Scheme-Monochrome-offblack, #505363)'};
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */

  @media screen and (max-width: 340px) {
    display: none;
  }
`;

const MetricsGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
`;

const ViewCount = styled.span`
  display: flex;
  padding: 2px 0px;
  align-items: center;
`;

const InquiryCount = styled.span`
  display: flex;
  padding: 2px 0px;
  align-items: center;
`;

const Count = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const SplitBarWrapper = styled.div`
  display: flex;
  padding: 0px 2px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey20, #ececf3);
  text-align: center;

  /* System/Medium/m-light */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 85%; /* 11.9px */
`;
